import React, { useState } from 'react';
import classNames from 'classnames';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import { gtmAccordionInteraction } from 'helpers/utils/gtm-events/gtmInteraction';
import { desktop } from 'helpers/utils/screensizes';

export interface AccordionItemProps {
  title: string;
  titleContent?: React.ReactNode;
  content: React.ReactNode;
  isDefaultOpen?: boolean;
}

export interface AccordionProps {
  items: AccordionItemProps[];
}

const AccordionItem: React.FC<AccordionItemProps> = ({ title, titleContent, content, isDefaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(isDefaultOpen);
  const [isDesktop] = useMediaQuery(desktop);

  return (
    <div className="border-b border-primary-gold py-8 first:border-t" data-testid={`accordion-item-${title}`}>
      <button
        className="flex w-full items-center justify-between text-left"
        onClick={() => {
          gtmAccordionInteraction({
            click_action: isOpen ? 'Collapse' : 'Expand',
            interaction_location: 'PDP',
            interaction_text: title,
          });
          setIsOpen(!isOpen);
        }}
      >
        <h3 className="text-[28px] font-semibold leading-[34px] text-[#222]">{title}</h3>
        {isDesktop && titleContent && !isOpen && <div data-testid="accordion-title-content">{titleContent}</div>}
        <span className="flex size-6 items-center justify-center rounded-full border-2 border-primary-dark text-2xl leading-none">
          {isOpen ? '−' : '+'}
        </span>
      </button>
      {!isDesktop && titleContent && !isOpen && <div data-testid="accordion-title-content">{titleContent}</div>}
      <div
        className={classNames(
          'grid transition-[grid-template-rows] duration-500 ease-in-out',
          isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
        )}
      >
        <div className="overflow-hidden">
          <div className="text-gray-600">{content}</div>
        </div>
      </div>
    </div>
  );
};

const Accordion: React.FC<AccordionProps> = ({ items }) => {
  return (
    <div data-testid="accordion">
      {items.map((item, index) => (
        <AccordionItem
          key={`${item.title}-${index}`}
          title={item.title}
          titleContent={item.titleContent}
          content={item.content}
          isDefaultOpen={item.isDefaultOpen}
        />
      ))}
    </div>
  );
};

export default Accordion;
